import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Upload, X, CheckCircle, Film, RefreshCw } from "lucide-react";
import { useCourses } from "@/hooks/useCourses";
import { supabase } from "@/services/supabase";
import SectionBackground from "@/components/layout/SectionBackground";

const TeamApplication = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const programParam = searchParams.get("program");
  
  const { courses } = useCourses();
  
  // Get invite-only courses for dropdown
  const inviteOnlyCourses = courses.filter(course => course.badge === "Invite Only");
  
  // Form state
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    city: "",
    country: "",
    danceSchool: "",
    program: programParam || "",
    instagram: "",
    facebook: "",
    otherSocial: "",
    background: "",
    videoLink: "",
  });
  
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cityAvailable, setCityAvailable] = useState<boolean | null>(null);
  const [checkingCity, setCheckingCity] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  // Pre-upload state
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  
  // Check city availability when city changes
  useEffect(() => {
    const checkCity = async () => {
      if (!formData.city || formData.city.length < 2) {
        setCityAvailable(null);
        return;
      }
      
      setCheckingCity(true);
      try {
        // Check if city has an active team
        // For now, we'll use a simple check - you can enhance this with actual team data
        const { data, error } = await supabase
          .from('user_locations')
          .select('city, country')
          .ilike('city', `%${formData.city}%`);
        
        if (error && error.code !== 'PGRST116') { // PGRST116 = table doesn't exist
          setCityAvailable(null);
          return;
        }
        
        // If we find locations with matching city, we assume there might be a team
        // This is a simple check - you can enhance this with actual team verification
        if (data && data.length > 0) {
          // Check if any of these locations are "official teams"
          // For now, we'll show the warning if any matching city exists
          setCityAvailable(false);
        } else {
          setCityAvailable(true);
        }
      } catch (error) {
        setCityAvailable(null);
      } finally {
        setCheckingCity(false);
      }
    };
    
    const timeoutId = setTimeout(checkCity, 500); // Debounce
    return () => clearTimeout(timeoutId);
  }, [formData.city, formData.country]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Check file size (max 200MB with direct R2 upload)
      const fileSizeMB = selectedFile.size / (1024 * 1024);
      if (selectedFile.size > 200 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          file: `File size (${fileSizeMB.toFixed(1)}MB) exceeds 200MB limit`
        }));
        return;
      }
      
      // Expanded allowed MIME types including mobile formats
      const allowedTypes = [
        'video/mp4', 
        'video/webm', 
        'video/quicktime',  // .mov files
        'video/x-msvideo',  // .avi
        'video/avi',        // alternate AVI MIME type
        'video/x-m4v',      // .m4v files (iPhone)
        'video/x-quicktime', // alternate QuickTime
        'video/3gpp',       // .3gp files
        'video/3gpp2',      // .3g2 files
        'video/mp2t',       // .mts files
        'video/mpeg',       // .mpeg files
        ''                  // Allow empty type (check extension instead)
      ];
      
      // Fallback extension checking for mobile devices that may not report MIME types correctly
      const allowedExtensions = ['.mp4', '.webm', '.mov', '.avi', '.m4v', '.3gp', '.3g2', '.mts', '.mpeg', '.mpg'];
      const fileExtension = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
      
      const isValidType = allowedTypes.includes(selectedFile.type);
      const isValidExtension = allowedExtensions.includes(fileExtension);
      
      if (!isValidType && !isValidExtension) {
        setErrors(prev => ({
          ...prev,
          file: `Video format not supported. Accepted formats: MP4, MOV, WebM, AVI, M4V, 3GP. Your file type: ${selectedFile.type || 'unknown'}`
        }));
        return;
      }
      
      setFile(selectedFile);
      setFilePreview(URL.createObjectURL(selectedFile));
      if (errors.file) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.file;
          return newErrors;
        });
      }
    }
  };
  
  const removeFile = () => {
    setFile(null);
    if (filePreview) {
      URL.revokeObjectURL(filePreview);
      setFilePreview(null);
    }
    // Clear upload state
    setUploadedFileUrl(null);
    setUploadStatus('idle');
    setUploadProgress(0);
  };
  
  // Pre-submit upload function
  const handleUploadNow = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (!file || uploadStatus === 'uploading') return;
    
    setUploadStatus('uploading');
    setUploadProgress(0);
    
    try {
      // Get presigned URL
      const { data: urlData, error: urlError } = await supabase.functions.invoke('upload-to-r2', {
        body: {
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
        },
      });
      
      if (urlError || !urlData?.presignedUrl) {
        throw new Error('Unable to prepare video upload');
      }
      
      // Upload with retry logic
      const maxRetries = 3;
      const baseDelay = 1000;
      
      const uploadWithRetry = async (attempt: number): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const percentComplete = Math.round((e.loaded / e.total) * 100);
              setUploadProgress(percentComplete);
            }
          });

          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              setUploadProgress(100);
              resolve();
            } else if (xhr.status >= 500 && attempt < maxRetries) {
              const delay = baseDelay * Math.pow(2, attempt);
              setTimeout(() => {
                setUploadProgress(0);
                uploadWithRetry(attempt + 1).then(resolve).catch(reject);
              }, delay);
            } else {
              reject(new Error(`Upload failed (${xhr.status})`));
            }
          });

          xhr.addEventListener('error', () => {
            if (attempt < maxRetries) {
              const delay = baseDelay * Math.pow(2, attempt);
              setTimeout(() => {
                setUploadProgress(0);
                uploadWithRetry(attempt + 1).then(resolve).catch(reject);
              }, delay);
            } else {
              reject(new Error('Upload failed after multiple attempts'));
            }
          });

          xhr.addEventListener('abort', () => {
            reject(new Error('Upload was cancelled'));
          });

          xhr.open('PUT', urlData.presignedUrl);
          xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
          xhr.send(file);
        });
      };

      await uploadWithRetry(0);
      
      setUploadedFileUrl(urlData.publicUrl);
      setUploadStatus('success');
    } catch (error) {
      console.error('Pre-upload failed:', error);
      setUploadStatus('error');
    }
  };
  
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }
    
    if (!formData.country.trim()) {
      newErrors.country = "Country is required";
    }
    
    if (!formData.program) {
      newErrors.program = "Please select a program";
    }
    
    if (!formData.background.trim()) {
      newErrors.background = "Please describe your dance background";
    } else if (formData.background.length < 50) {
      newErrors.background = "Please provide at least 50 characters about your dance background";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Warn user if city already has a team
    if (cityAvailable === false && !confirm(
      "This city may already have an active Klau & Ros Team. Do you still want to submit your application?"
    )) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Use pre-uploaded URL if available, otherwise upload during submit
      let fileUrl = uploadedFileUrl;
      
      if (file && !uploadedFileUrl) {
        // Fallback: upload during submission if not pre-uploaded
        setIsUploading(true);
        setUploadProgress(0);

        try {
          const { data: urlData, error: urlError } = await supabase.functions.invoke('upload-to-r2', {
            body: {
              fileName: file.name,
              contentType: file.type || 'application/octet-stream',
            },
          });

          if (urlError || !urlData?.presignedUrl) {
            throw new Error('Unable to prepare video upload. Please try again or use a video link instead.');
          }

          const maxRetries = 3;
          const baseDelay = 1000;
          
          const uploadWithRetry = async (attempt: number): Promise<void> => {
            return new Promise<void>((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              
              xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable) {
                  const percentComplete = Math.round((e.loaded / e.total) * 100);
                  setUploadProgress(percentComplete);
                }
              });

              xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                  setUploadProgress(100);
                  resolve();
                } else if (xhr.status >= 500 && attempt < maxRetries) {
                  const delay = baseDelay * Math.pow(2, attempt);
                  setTimeout(() => {
                    setUploadProgress(0);
                    uploadWithRetry(attempt + 1).then(resolve).catch(reject);
                  }, delay);
                } else {
                  reject(new Error(`Video upload failed (${xhr.status}). Please try a smaller file or use a video link.`));
                }
              });

              xhr.addEventListener('error', () => {
                if (attempt < maxRetries) {
                  const delay = baseDelay * Math.pow(2, attempt);
                  setTimeout(() => {
                    setUploadProgress(0);
                    uploadWithRetry(attempt + 1).then(resolve).catch(reject);
                  }, delay);
                } else {
                  reject(new Error('Video upload failed after multiple attempts. Please check your connection and try again.'));
                }
              });

              xhr.addEventListener('abort', () => {
                reject(new Error('Video upload was cancelled.'));
              });

              xhr.open('PUT', urlData.presignedUrl);
              xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
              xhr.send(file);
            });
          };

          await uploadWithRetry(0);
          fileUrl = urlData.publicUrl;
        } catch (error) {
          throw error;
        } finally {
          setIsUploading(false);
        }
      }
      
      // Submit application via Edge Function
      const { data, error } = await supabase.functions.invoke('submit-team-application', {
        body: {
          ...formData,
          videoFileUrl: fileUrl,
          fileName: file?.name || null,
        },
      });
      
      if (error) {
        throw new Error(error.message || 'Failed to submit application');
      }
      
      // Success - show confirmation and redirect
      alert("Thank you for your application! Our team will review your request and confirm availability for your city within 3–5 business days. You should receive a confirmation email shortly.");
      navigate('/courses');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit application. Please try again.';
      alert(`Application Error: ${errorMessage}\n\nIf the problem persists, please email klauryros@gmail.com with your application details.`);
    } finally {
      setIsSubmitting(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };
  
  return (
    <main className="relative min-h-screen pt-20 lg:pt-[88px]">
      <SectionBackground />
      
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4"
              style={{
                fontFamily: "serif",
                color: "#FFFFFF",
              }}
            >
              Team Application
            </h1>
            <p
              className="text-lg md:text-xl"
              style={{
                fontFamily: "'Grift', sans-serif",
                color: "rgba(255, 255, 255, 0.7)",
              }}
            >
              Apply to join an exclusive Klau & Ros team in your city
            </p>
          </div>
          
          {/* Application Form */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="space-y-6"
          >
            {/* Full Name */}
            <div>
              <label
                htmlFor="fullName"
                className="block mb-2 text-sm font-semibold"
                style={{
                  fontFamily: "'Grift', sans-serif",
                  color: "#FFFFFF",
                }}
              >
                Full Name *
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                style={{
                  fontFamily: "'Grift', sans-serif",
                }}
                placeholder="Enter your full name"
              />
              {errors.fullName && (
                <p className="mt-1 text-sm text-red-400">{errors.fullName}</p>
              )}
            </div>
            
            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block mb-2 text-sm font-semibold"
                style={{
                  fontFamily: "'Grift', sans-serif",
                  color: "#FFFFFF",
                }}
              >
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                style={{
                  fontFamily: "'Grift', sans-serif",
                }}
                placeholder="your.email@example.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-400">{errors.email}</p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label
                htmlFor="phone"
                className="block mb-2 text-sm font-semibold"
                style={{
                  fontFamily: "'Grift', sans-serif",
                  color: "#FFFFFF",
                }}
              >
                Phone Number (optional)
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                style={{
                  fontFamily: "'Grift', sans-serif",
                }}
                placeholder="+1 234 567 8900"
              />
            </div>
            
            {/* City and Country */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="city"
                  className="block mb-2 text-sm font-semibold"
                  style={{
                    fontFamily: "'Grift', sans-serif",
                    color: "#FFFFFF",
                  }}
                >
                  City *
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  style={{
                    fontFamily: "'Grift', sans-serif",
                  }}
                  placeholder="Enter your city"
                />
                {errors.city && (
                  <p className="mt-1 text-sm text-red-400">{errors.city}</p>
                )}
                {checkingCity && (
                  <p className="mt-1 text-sm" style={{ color: "rgba(255, 255, 255, 0.6)" }}>
                    Checking city availability...
                  </p>
                )}
                {cityAvailable === false && !checkingCity && (
                  <div className="mt-2 p-3 rounded-lg border border-yellow-500/50 bg-yellow-500/10">
                    <p className="text-sm" style={{ color: "#FCD34D" }}>
                      ⚠️ This city may already have an active Klau & Ros Team. Please check your nearby areas or contact management for exceptions. You can still submit your application if you'd like to be considered.
                    </p>
                  </div>
                )}
                {cityAvailable === true && !checkingCity && (
                  <p className="mt-1 text-sm" style={{ color: "#22C55E" }}>
                    ✓ City appears to be available
                  </p>
                )}
              </div>
              
              <div>
                <label
                  htmlFor="country"
                  className="block mb-2 text-sm font-semibold"
                  style={{
                    fontFamily: "'Grift', sans-serif",
                    color: "#FFFFFF",
                  }}
                >
                  Country *
                </label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  style={{
                    fontFamily: "'Grift', sans-serif",
                  }}
                  placeholder="Enter your country"
                />
                {errors.country && (
                  <p className="mt-1 text-sm text-red-400">{errors.country}</p>
                )}
              </div>
            </div>
            
            {/* Dance School */}
            <div>
              <label
                htmlFor="danceSchool"
                className="block mb-2 text-sm font-semibold"
                style={{
                  fontFamily: "'Grift', sans-serif",
                  color: "#FFFFFF",
                }}
              >
                Name of Dance School (if applicable)
              </label>
              <input
                type="text"
                id="danceSchool"
                name="danceSchool"
                value={formData.danceSchool}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                style={{
                  fontFamily: "'Grift', sans-serif",
                }}
                placeholder="Enter dance school name (optional)"
              />
            </div>
            
            {/* Program Selection */}
            <div>
              <label
                htmlFor="program"
                className="block mb-2 text-sm font-semibold"
                style={{
                  fontFamily: "'Grift', sans-serif",
                  color: "#FFFFFF",
                }}
              >
                Which program are you applying for? *
              </label>
              <select
                id="program"
                name="program"
                value={formData.program}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                style={{
                  fontFamily: "'Grift', sans-serif",
                }}
              >
                <option value="">Select a program</option>
                {inviteOnlyCourses.map((course) => (
                  <option key={course.id} value={course.title} style={{ color: "#000000" }}>
                    {course.title}
                  </option>
                ))}
              </select>
              {errors.program && (
                <p className="mt-1 text-sm text-red-400">{errors.program}</p>
              )}
            </div>
            
            {/* Social Media Links */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="instagram"
                  className="block mb-2 text-sm font-semibold"
                  style={{
                    fontFamily: "'Grift', sans-serif",
                    color: "#FFFFFF",
                  }}
                >
                  Instagram
                </label>
                <input
                  type="text"
                  id="instagram"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  style={{
                    fontFamily: "'Grift', sans-serif",
                  }}
                  placeholder="@username (optional)"
                />
              </div>
              
              <div>
                <label
                  htmlFor="facebook"
                  className="block mb-2 text-sm font-semibold"
                  style={{
                    fontFamily: "'Grift', sans-serif",
                    color: "#FFFFFF",
                  }}
                >
                  Facebook
                </label>
                <input
                  type="text"
                  id="facebook"
                  name="facebook"
                  value={formData.facebook}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  style={{
                    fontFamily: "'Grift', sans-serif",
                  }}
                  placeholder="Facebook profile URL (optional)"
                />
              </div>
            </div>
            
            <div>
              <label
                htmlFor="otherSocial"
                className="block mb-2 text-sm font-semibold"
                style={{
                  fontFamily: "'Grift', sans-serif",
                  color: "#FFFFFF",
                }}
              >
                Other Social Media Links
              </label>
              <input
                type="text"
                id="otherSocial"
                name="otherSocial"
                value={formData.otherSocial}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                style={{
                  fontFamily: "'Grift', sans-serif",
                }}
                placeholder="TikTok, YouTube, etc. (optional)"
              />
            </div>
            
            {/* Dance Background */}
            <div>
              <label
                htmlFor="background"
                className="block mb-2 text-sm font-semibold"
                style={{
                  fontFamily: "'Grift', sans-serif",
                  color: "#FFFFFF",
                }}
              >
                Short Description of Your Dance Background *
              </label>
              <textarea
                id="background"
                name="background"
                value={formData.background}
                onChange={handleChange}
                rows={6}
                className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none"
                style={{
                  fontFamily: "'Grift', sans-serif",
                }}
                placeholder="Tell us about your dance experience, training, teaching experience, and why you want to join (minimum 50 characters)"
              />
              {errors.background && (
                <p className="mt-1 text-sm text-red-400">{errors.background}</p>
              )}
              <p className="mt-1 text-xs" style={{ color: "rgba(255, 255, 255, 0.5)" }}>
                {formData.background.length}/50 minimum characters
              </p>
            </div>
            
            {/* Video Link */}
            <div>
              <label
                htmlFor="videoLink"
                className="block mb-2 text-sm font-semibold"
                style={{
                  fontFamily: "'Grift', sans-serif",
                  color: "#FFFFFF",
                }}
              >
                Link to Video (Optional)
              </label>
              <input
                type="url"
                id="videoLink"
                name="videoLink"
                value={formData.videoLink}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                style={{
                  fontFamily: "'Grift', sans-serif",
                }}
                placeholder="https://youtube.com/watch?v=... or Vimeo link"
              />
            </div>
            
            {/* File Upload */}
            <div>
              <label
                className="block mb-2 text-sm font-semibold"
                style={{
                  fontFamily: "'Grift', sans-serif",
                  color: "#FFFFFF",
                }}
              >
                Upload Video File (Optional)
              </label>
              
              {/* No file selected - show choose button */}
              {!file && uploadStatus === 'idle' && (
                <div className="flex items-center gap-4">
                  <label
                    htmlFor="file"
                    className="flex items-center gap-2 px-6 py-3 rounded-xl cursor-pointer transition-all border-2 border-dashed border-white/30 hover:border-orange-500/50"
                    style={{
                      background: "rgba(255, 255, 255, 0.05)",
                      fontFamily: "'Grift', sans-serif",
                      color: "#FFFFFF",
                    }}
                  >
                    <Upload size={20} />
                    <span>Choose File</span>
                  </label>
                  <input
                    type="file"
                    id="file"
                    accept="video/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              )}
              
              {/* File selected but not uploaded - show upload now button */}
              {file && uploadStatus === 'idle' && (
                <div className="p-4 rounded-xl bg-white/5 border border-white/20">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 rounded-lg bg-white/10 shrink-0">
                        <Film size={20} className="text-white/70" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate">{file.name}</p>
                        <p className="text-white/50 text-xs">
                          {(file.size / (1024 * 1024)).toFixed(1)}MB
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={(e) => handleUploadNow(e)}
                        className="px-4 py-2 rounded-lg bg-orange-500 text-black text-sm font-semibold hover:bg-orange-400 transition-colors"
                        style={{ fontFamily: "'Grift', sans-serif" }}
                      >
                        Upload Now
                      </button>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                      >
                        <X size={18} className="text-white/70" />
                      </button>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-white/40">
                    Tip: Upload now to ensure your video is ready before submitting
                  </p>
                </div>
              )}
              
              {/* Upload in progress */}
              {uploadStatus === 'uploading' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/30"
                >
                  <div className="flex items-center justify-between text-sm mb-3">
                    <div className="flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin text-orange-500" />
                      <span className="text-white">Uploading {file?.name}...</span>
                    </div>
                    <span className="font-semibold text-orange-500">{uploadProgress}%</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${uploadProgress}%` }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                    />
                  </div>
                </motion.div>
              )}
              
              {/* Upload successful */}
              {uploadStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-4 rounded-xl bg-green-500/10 border border-green-500/30"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 rounded-lg bg-green-500/20 shrink-0">
                        <CheckCircle size={20} className="text-green-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-green-500 font-medium text-sm">Video ready!</p>
                        <p className="text-white/70 text-xs truncate">{file?.name}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeFile}
                      className="p-2 rounded-lg hover:bg-white/10 transition-colors shrink-0"
                    >
                      <X size={18} className="text-white/50" />
                    </button>
                  </div>
                </motion.div>
              )}
              
              {/* Upload error */}
              {uploadStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-4 rounded-xl bg-red-500/10 border border-red-500/30"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 rounded-lg bg-red-500/20 shrink-0">
                        <X size={20} className="text-red-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-red-400 font-medium text-sm">Upload failed</p>
                        <p className="text-white/50 text-xs">Try again or use a video link instead</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={(e) => handleUploadNow(e)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 text-sm hover:bg-red-500/30 transition-colors"
                      >
                        <RefreshCw size={14} />
                        Retry
                      </button>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                      >
                        <X size={18} className="text-white/50" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
              
              {errors.file && (
                <p className="mt-2 text-sm text-red-400">{errors.file}</p>
              )}
              <p className="mt-2 text-xs" style={{ color: "rgba(255, 255, 255, 0.5)" }}>
                Maximum file size: 200MB. Supported formats: MP4, MOV, WebM, AVI, M4V, 3GP
              </p>
            </div>

            {/* Upload Progress during form submission (fallback) */}
            {isUploading && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-2 p-4 rounded-xl bg-secondary/20 border border-primary/20"
              >
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground">Uploading video...</span>
                  <span className="font-semibold text-primary">{uploadProgress}%</span>
                </div>
                <div className="w-full h-2 bg-secondary/50 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-primary to-primary/80 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${uploadProgress}%` }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  />
                </div>
              </motion.div>
            )}
            
            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={isSubmitting || isUploading}
                className="w-full py-4 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: isSubmitting
                    ? "rgba(230, 126, 34, 0.5)"
                    : "linear-gradient(135deg, #E67E22 0%, #FF9E4F 100%)",
                  color: "#000000",
                  fontFamily: "'Grift', sans-serif",
                  boxShadow: isSubmitting
                    ? "none"
                    : "0 4px 30px rgba(230, 126, 34, 0.4)",
                }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Submitting Application...
                  </>
                ) : (
                  "Submit Application"
                )}
              </button>
            </div>
          </motion.form>
        </motion.div>
      </div>
    </main>
  );
};

export default TeamApplication;

